<template>
  <Page>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <div class="title-container">
            <div class="title">หมวดหมู่</div>
          </div>
          <div class="actions">
            <router-link
              :to="'/taxonomy/create'"
              class="btn btn-primary">
              <i class="fas fa-plus icon"></i> สร้างหมวดหมู่
            </router-link>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <PreloaderSection v-if="loadTaxonomyState.isLoading" />
      <div v-if="!loadTaxonomyState.isLoading">
        <!-- render content -->
        <Section v-if="!loadTaxonomyState.error">
          <template v-slot:section-filter>
            <input
              class="form-control form-control-search"
              placeholder="ค้นหาด้วยชื่อหมวดหมู่..."
              @keyup.enter.stop.prevent="onSearch"/>
          </template>
          <!-- render content -->
          <template v-slot:section-body>
            <div v-if="!isEmpty()">
              <List>
                <template v-slot:list-header>
                  <div class="list-item-detail">
                    <div class="list-item list-item-primary list-item-title">ชื่อหมวดหมู่</div>
                    <div class="list-item">คีย์</div>
                    <div class="list-item">สร้างเมื่อ</div>
                    <div class="list-item list-item-action"></div>
                  </div>
                </template>
                <template v-slot:list-body>
                  <div
                    class="list-item-container no-underline"
                    v-for="item in taxonomy"
                    :key="item.id">
                    <div class="list-item-detail">
                      <div
                        @click.prevent="onClickRow(item.key)"
                        class="list-item list-item-primary list-item-title">
                        {{item.name}}
                      </div>
                      <div
                        @click.prevent="onClickRow(item.key)"
                        class="list-item">
                        {{item.key}}
                      </div>
                      <div
                        @click.prevent="onClickRow(item.key)"
                        class="list-item">
                        {{formatDateTime(item.createdAt)}}
                      </div>
                      <div class="list-item list-item-action">
                        <div class="action-container">
                          <router-link
                            class="btn btn-light btn-action"
                            :to="`/taxonomy/${item.key}/edit`">
                            <i class="fas fa-pencil-alt"></i>
                          </router-link>
                          <router-link
                            class="btn btn-light btn-action"
                            :to="`/taxonomy/${item.key}`">
                            <i class="fas fa-chevron-right"></i>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </List>
              <div
                class="pagination-container"
                v-if="isShowPagination()">
                <Pagination :lastPage="lastPage" />
              </div>
            </div>
            <!-- empty list --->
            <EmptyMessage v-if="isEmpty()">
              <h3>ไม่มีข้อมูล</h3>
              <router-link
                class="btn btn-light btn-lg mt-4"
                :to="`/taxonomy/create`">
                สร้างหมวดหมู่
              </router-link>
            </EmptyMessage>
            <!-- search result -->
            <EmptyMessage v-if="isEmptySearch()">
              <h3>ไม่พบข้อความที่ค้นหา</h3>
              <button
                type="button"
                class="btn btn-light btn-lg mt-4"
                @click="clearSearch">
                ล้างการค้นหา
              </button>
            </EmptyMessage>
          </template>
        </Section>
        <!-- error from api -->
        <EmptyMessage v-if="loadTaxonomyState.error && !isLoading">
          <h3>เกิดข้อผิดพลาดขณะโหลดข้อมูล</h3>
          <router-link
            :to="`/taxonomy/`"
            class="btn btn-light btn-lg mt-4">
            กลับสู่หน้าแท็ก
          </router-link>
          <span
            slot="code"
            class="code">
            Code : {{loadTaxonomyState.error.statusCode || 'not found' }}
          </span>
        </EmptyMessage>
      </div>
    </div>
  </Page>
</template>

<script>
import _ from 'lodash';
import dateFormetter from '@/utils/dateFormatter';
import Page from '@/layouts/Page.vue';
import Pagination from '@/components/Pagination.vue';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';

import { loadTaxonomies } from './api';

export default {
  mixins: [dateFormetter],
  components: {
    Page,
    Pagination,
    Section,
    List,
    EmptyMessage,
    PreloaderSection,
  },
  data() {
    return {
      errorCode: null,
      taxonomy: null,
      lastPage: null,
      loadTaxonomyState: {
        isLoading: true,
        error: null,
      },
    };
  },
  computed: {
    filter() {
      return this.$route.query;
    },
  },
  watch: {
    filter(newValue) {
      this.load(newValue);
    },
  },
  created() {
    this.load(this.filter);
  },
  methods: {
    async load(filter) {
      try {
        const response = await loadTaxonomies(filter);
        this.taxonomy = response.data.data;
        this.lastPage = response.data.lastPage;
        this.loadTaxonomyState.isLoading = false;
      } catch (error) {
        this.loadTaxonomyState.error = error.response;
        this.loadTaxonomyState.isLoading = false;
      }
    },
    onClickRow(key) {
      this.$router.push(`/taxonomy/${key}`);
    },
    isEmptySearch() {
      return _.isEmpty(this.taxonomy) && !_.every(this.filter, _.isEmpty);
    },
    isEmpty() {
      return _.isEmpty(this.taxonomy) && _.every(this.filter, _.isEmpty);
    },
    onSearch(event) {
      const filter = { ...this.filter, q: event.target.value };
      this.$router.push({ query: { ...filter } });
    },
    clearSearch() {
      const filter = null;
      this.$router.push({ query: { ...filter } });
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/layouts/page.scss';
@import './src/assets/scss/themes/components/form.scss';

@media (max-width: 575.98px) {
  .list-item-container {
    .list-item-detail {
      .list-item {
        font-size: $font-size-base * 0.875;
        display: none;

        &.list-item-primary {
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          padding: $spacer * 0.25;
        }

        &.list-item-secondary {
          display: flex;
          order: -1;
          padding: $spacer * 0.25;
          font-size: $font-size-base * 0.875;
        }

        &.list-item-status {
          display: flex;
        }
      }
    }
  }
}
</style>
